import React, { useEffect, useRef, useState, useCallback } from 'react';

interface VoiceInputButtonProps {
  /** Called with the latest cumulative transcript text as the user speaks. */
  onTranscript: (text: string) => void;
  /** Called once when the user starts speaking (transitions to listening). */
  onStart?: () => void;
  /** Called once when recognition ends (either by user click or by silence timeout). */
  onEnd?: () => void;
  /** Tailwind text colour class for the listening pulse, e.g. "text-brand-500". */
  primaryColor?: string;
  /** BCP 47 language tag, defaults to browser locale. */
  lang?: string;
  /** ARIA label override. */
  ariaLabel?: string;
}

// Minimal local type — DOM lib doesn't ship Web Speech API types in tsconfig "lib": "DOM" yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SpeechRecognitionLike = any;

interface SpeechRecognitionEventResult {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
  }>;
}

const isSupported = (): boolean => {
  if (typeof window === 'undefined') return false;
  return Boolean(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  );
};

const MicIdleIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const MicListeningIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  onTranscript,
  onStart,
  onEnd,
  primaryColor,
  lang,
  ariaLabel,
}) => {
  const [supported] = useState<boolean>(() => isSupported());
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const baselineRef = useRef<string>('');

  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      // recognition may already be stopped — swallow
    }
  }, []);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const start = useCallback(
    (currentText: string) => {
      if (!supported) return;
      setError(null);
      // Keep whatever the user has already typed; voice appends.
      baselineRef.current = currentText.trim().length > 0 ? currentText + ' ' : '';

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Ctor: any =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognition: SpeechRecognitionLike = new Ctor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = lang || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

      recognition.onresult = (event: SpeechRecognitionEventResult) => {
        let finalText = '';
        let interimText = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          const text = r[0].transcript;
          if (r.isFinal) finalText += text;
          else interimText += text;
        }
        // Accumulate finalised text into the baseline so the textarea grows naturally.
        if (finalText) baselineRef.current += finalText;
        onTranscript((baselineRef.current + interimText).trim());
      };

      recognition.onerror = (event: { error?: string }) => {
        setError(event.error || 'recognition_error');
        setListening(false);
        onEnd?.();
      };

      recognition.onend = () => {
        setListening(false);
        onEnd?.();
      };

      recognition.start();
      recognitionRef.current = recognition;
      setListening(true);
      onStart?.();
    },
    [lang, onEnd, onStart, onTranscript, supported]
  );

  if (!supported) {
    return null;
  }

  const colourClass = primaryColor && primaryColor.startsWith('text-') ? primaryColor : undefined;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => {
          if (listening) {
            stop();
          } else {
            // We don't have direct access to the textarea ref here; consumer is expected to
            // pass current text via onTranscript by calling start with the latest comment.
            // For simplicity we start with empty baseline and append from there. Consumers
            // can prepend manually if needed. The wiring in WidgetRenderer passes the live
            // `comment` value into `start` via the wrapper below.
            start('');
          }
        }}
        aria-pressed={listening}
        aria-label={ariaLabel || (listening ? 'Stop recording' : 'Record voice feedback')}
        title={listening ? 'Stop recording' : 'Record voice feedback'}
        className={`flex items-center justify-center h-8 w-8 rounded-full border transition-all ${
          listening
            ? 'bg-red-500 text-white border-red-500 animate-pulse'
            : 'bg-white text-slate-500 border-slate-200 hover:text-slate-700 hover:border-slate-300'
        } ${colourClass || ''}`}
      >
        {listening ? <MicListeningIcon /> : <MicIdleIcon />}
      </button>
      <span className="text-xs text-slate-400 select-none">
        {listening ? 'Listening… click to stop' : 'or speak it'}
      </span>
      {error && (
        <span className="text-xs text-red-500 select-none">
          ({error === 'not-allowed' ? 'mic blocked' : error})
        </span>
      )}
    </div>
  );
};

export default VoiceInputButton;
